/**
 * Notification → view routing — the pure "where does clicking this land"
 * map for the top-bar notifications bell.
 *
 * Kept out of the component so it can be unit-tested and so the mapping is
 * explicit in one place: approvals + scan findings live on the Dashboard
 * (approvals inbox / needs-attention feed), at-risk customers live in the
 * Customer Success cockpit, and overdue follow-ups live in the Contacts CRM.
 * Type-only imports, so this stays a zero-dependency pure module.
 */

import type { NotificationKind } from './notifications';
import type { ViewKey } from '../../components/TopMenu';
import type { ContactsTab } from '../../views/PeopleView';

export interface NotificationRoute {
  view: ViewKey;
  /** When landing on the Contacts CRM, which sub-tab to pre-select. */
  contactsTab?: ContactsTab;
}

/** Where a notification of this kind deep-links when clicked. */
export function notificationRoute(kind: NotificationKind): NotificationRoute {
  switch (kind) {
    case 'approval':
      // The approvals triage inbox lives on the Dashboard.
      return { view: 'dashboard' };
    case 'mention':
      // @mentions land on the Projects surface where the comment lives.
      return { view: 'projects' };
    case 'at-risk':
      // Health cockpit — riskiest customers ranked first.
      return { view: 'customer-success' };
    case 'overdue':
      // Overdue follow-ups are contact records in the CRM.
      return { view: 'contacts', contactsTab: 'contacts' };
    case 'finding':
      // Scan findings surface in the Dashboard's needs-attention feed.
      return { view: 'dashboard' };
  }
}
